import React, { useState } from "react";
import { PATTERNS, LEVERAGE_TYPES } from "./data.js";

export default function ObservationStage({ onComplete }) {
  const [index, setIndex] = useState(0);
  const [worthSolving, setWorthSolving] = useState(null);
  const [typeChoice, setTypeChoice] = useState(null);
  const [correctCount, setCorrectCount] = useState(0);
  const [flash, setFlash] = useState(null);

  const current = PATTERNS[index];
  const done = index >= PATTERNS.length;

  const resolveAndAdvance = (finalWorthSolving, finalType) => {
    const correct =
      finalWorthSolving === current.correctWorthSolving &&
      (!current.correctWorthSolving || finalType === current.correctType);
    if (correct) setCorrectCount((c) => c + 1);
    setFlash(correct ? "correct" : "off");
    setTimeout(() => {
      setFlash(null);
      setWorthSolving(null);
      setTypeChoice(null);
      setIndex((i) => i + 1);
    }, 800);
  };

  const chooseWorthSolving = (value) => {
    setWorthSolving(value);
    if (!value) {
      // No follow-up question needed — resolve immediately.
      resolveAndAdvance(value, null);
    }
  };

  const chooseType = (typeId) => {
    setTypeChoice(typeId);
    resolveAndAdvance(worthSolving, typeId);
  };

  if (done) {
    return (
      <div className="animate-popIn text-center">
        <p className="label-pixel mb-2 text-lime">Observation Complete</p>
        <h2 className="pixel-heading mb-4 text-base text-mist">Patterns Logged</h2>

        <div className="mx-auto mb-6 max-w-sm space-y-2 border-2 border-black bg-ink p-4 text-left text-sm">
          <div className="flex justify-between">
            <span className="text-mist/60">Read Correctly</span>
            <span className="text-lime">
              {correctCount}/{PATTERNS.length}
            </span>
          </div>
        </div>

        <p className="mx-auto mb-6 max-w-sm text-xs text-mist/60">
          Leverage isn't luck — it's noticing what everyone else scrolls past. Now pick one
          pattern and actually build toward it.
        </p>

        <button onClick={() => onComplete({ correctCount })} className="btn-magenta">
          CHOOSE YOUR DIRECTION ▶
        </button>
      </div>
    );
  }

  return (
    <div>
      <div className="mb-4 flex items-center justify-between font-display text-[9px] text-mist/60">
        <span>OBSERVATION</span>
        <span>
          {index}/{PATTERNS.length} LOGGED
        </span>
      </div>

      <p className="mb-5 text-xs text-mist/60">
        Six things you noticed this week. For each: is it a real, repeating pattern worth
        solving — and if so, what kind of leverage would solving it build?
      </p>

      <div
        key={current.id}
        className={`mb-5 border-2 border-black bg-ink p-5 transition-colors ${
          flash === "correct" ? "border-lime shadow-glow-cyan" : flash === "off" ? "border-danger" : ""
        }`}
      >
        <p className="text-sm leading-relaxed text-mist/80">{current.text}</p>
        {flash && <p className="mt-3 text-[11px] text-mist/50">{current.note}</p>}
      </div>

      {!flash && (
        <>
          <div className="mb-4">
            <p className="label-pixel mb-2 text-mist/60">Worth Solving?</p>
            <div className="grid grid-cols-2 gap-2">
              <button
                onClick={() => chooseWorthSolving(true)}
                className={`border-2 px-3 py-2 font-display text-[9px] uppercase transition-colors ${
                  worthSolving === true ? "border-cyan bg-cyan text-ink" : "border-mist/30 text-mist/70 hover:border-cyan"
                }`}
              >
                Yes, Real Pattern
              </button>
              <button
                onClick={() => chooseWorthSolving(false)}
                className={`border-2 px-3 py-2 font-display text-[9px] uppercase transition-colors ${
                  worthSolving === false ? "border-mist bg-mist/20 text-mist" : "border-mist/30 text-mist/70 hover:border-mist"
                }`}
              >
                No, Just Noise
              </button>
            </div>
          </div>

          {worthSolving === true && (
            <div>
              <p className="label-pixel mb-2 text-mist/60">What Kind of Leverage?</p>
              <div className="grid grid-cols-2 gap-2">
                {LEVERAGE_TYPES.map((t) => (
                  <button
                    key={t.id}
                    onClick={() => chooseType(t.id)}
                    className={`border-2 px-2 py-2 text-left font-display text-[8px] uppercase transition-colors ${
                      typeChoice === t.id ? "border-magenta bg-magenta text-ink" : "border-mist/30 text-mist/70 hover:border-magenta"
                    }`}
                  >
                    {t.icon} {t.label}
                  </button>
                ))}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
